import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData, type QueryKey } from '@tanstack/react-query';
import { AlarmClock, Bell, BellRing, Check, ExternalLink, Eye, EyeOff } from 'lucide-react';
import {
  STAFF_ALERT_EVENT,
  STAFF_ALERT_SNOOZE_MINUTES,
  STAFF_ALERT_TRAY_VISIBLE,
  STAFF_DUTY_EVENT,
  staffAlertPath,
  type StaffAlertDto,
  type StaffAlertFeed,
  type StaffAlertSnoozeMinutes,
  type StaffDuty,
} from '@his/shared';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { useTranslation, type Translator } from '@/lib/i18n';
import { fallbackInterval, useRealtimeInvalidate } from '@/lib/realtime';
import { cn, relative } from '@/lib/utils';
import { DueStrips, dueBellLabel, useDueNotice } from '@/features/ipd/tasks/DueNotice';
import { Button, Popover, PopoverContent, PopoverTrigger, StatusBadge } from './ui/primitives';
import { useToast } from './ui/toast';
import { boldParams, delayUntil, messageSegments, severityTone, trayView, unreadLabel, withoutAlert } from './staff-alert-view';

const FEED_KEY: QueryKey = ['staff-alerts'];
const DUTY_KEY: QueryKey = ['staff-duty'];
const FEED_ONLY: QueryKey[] = [FEED_KEY];
const FEED_AND_DUTY: QueryKey[] = [FEED_KEY, DUTY_KEY];
const FALLBACK_POLL_MS = 120_000;
const FRESH_ON_CONNECT_MS = 5_000;

export function StaffAlertBell() {
  const { user, hasModule } = useAuth();
  if (!user?.tenantId || user.isPlatformUser || !(hasModule('ipd') || hasModule('opd'))) return null;
  return <StaffAlertCenter userId={user.id} />;
}

function useTimedRefresh(at: string | null | undefined, keys: QueryKey[]) {
  const queryClient = useQueryClient();
  const pending = useRef(false);
  useEffect(() => {
    const delay = delayUntil(at);
    if (delay === null) return undefined;
    const refresh = () => {
      for (const queryKey of keys) void queryClient.invalidateQueries({ queryKey });
    };
    const timer = setTimeout(() => {
      if (document.visibilityState === 'visible') refresh();
      else pending.current = true;
    }, delay);
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || !pending.current) return;
      pending.current = false;
      refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [at, keys, queryClient]);
}

function StaffAlertCenter({ userId }: { userId: string }) {
  const { t } = useTranslation('notifications');
  const { can, hasModule } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [hideNonCritical, setHideNonCritical] = useState(false);

  const duty = useQuery({
    queryKey: DUTY_KEY,
    queryFn: () => get<StaffDuty>('/staff-alerts/me/duty'),
    staleTime: 5 * 60_000,
  });
  const channels = [`user:${userId}`, ...(duty.data?.wards ?? []).map((ward) => `ward:${ward.id}`)];
  const connected = useRealtimeInvalidate(channels, [STAFF_ALERT_EVENT, STAFF_DUTY_EVENT], FEED_ONLY);
  useRealtimeInvalidate([`user:${userId}`], [STAFF_DUTY_EVENT], [DUTY_KEY]);
  const dutyWards = duty.data?.wards ?? [];
  const tracksDueTasks = hasModule('ipd') && can('emar.read', 'emar.administer', 'nursing.note.write') && dutyWards.length > 0;
  const due = useDueNotice(dutyWards, tracksDueTasks);
  const dueLabel = dueBellLabel(due.total);

  const feed = useInfiniteQuery({
    queryKey: FEED_KEY,
    queryFn: ({ pageParam }) => get<StaffAlertFeed>(`/staff-alerts${qs({ cursor: pageParam })}`),
    initialPageParam: '',
    getNextPageParam: (last: StaffAlertFeed) => last.nextCursor ?? undefined,
    refetchInterval: fallbackInterval(connected, FALLBACK_POLL_MS),
  });

  const wasConnected = useRef(connected);
  const updatedAt = feed.dataUpdatedAt;
  useEffect(() => {
    if (connected && !wasConnected.current && Date.now() - updatedAt > FRESH_ON_CONNECT_MS) {
      void queryClient.invalidateQueries({ queryKey: FEED_KEY });
      void queryClient.invalidateQueries({ queryKey: DUTY_KEY });
    }
    wasConnected.current = connected;
  }, [connected, updatedAt, queryClient]);

  const pages = feed.data?.pages ?? [];
  useTimedRefresh(pages[0]?.wakeAt, FEED_ONLY);
  useTimedRefresh(duty.data?.changesAt, FEED_AND_DUTY);

  const removeLocally = (alertId: string) =>
    queryClient.setQueryData<InfiniteData<StaffAlertFeed>>(FEED_KEY, (data) => (data ? { ...data, pages: withoutAlert(data.pages, alertId) } : data));
  const failed = (error: unknown) => {
    toast.error(t('staff_alert.could_not_update'), describeError(error));
    void queryClient.invalidateQueries({ queryKey: FEED_KEY });
  };

  const acknowledge = useMutation({
    mutationFn: (alertId: string) => post(`/staff-alerts/${alertId}/ack`, {}),
    onMutate: removeLocally,
    onError: failed,
  });
  const snooze = useMutation({
    mutationFn: (input: { alertId: string; minutes: StaffAlertSnoozeMinutes }) => post(`/staff-alerts/${input.alertId}/snooze`, { minutes: input.minutes }),
    onMutate: (input) => removeLocally(input.alertId),
    onError: failed,
    onSuccess: () => {
      if (!connected) void queryClient.invalidateQueries({ queryKey: FEED_KEY });
    },
  });

  const alerts = pages.flatMap((page) => page.data);
  const unread = pages[0]?.unread ?? null;
  const badge = unreadLabel(unread);
  const view = trayView(alerts, { hideNonCritical, max: STAFF_ALERT_TRAY_VISIBLE });
  const more = Math.max(view.more, (unread ?? alerts.length) - view.shown.length - view.hidden);
  const hasNonCritical = alerts.some((alert) => alert.severity !== 'critical');

  const openPath = (path: string) => {
    setOpen(false);
    navigate(path);
  };
  const changeOpen = (next: boolean) => {
    setOpen(next);
    if (next) due.refreshIfOld();
  };
  const bellLabel = badge ? t('staff_alert.unread', { count: unread ?? 0 }) : t('staff_alert.bell');
  const cardProps = {
    t,
    onOpen: openPath,
    onAcknowledge: (alertId: string) => acknowledge.mutate(alertId),
    onSnooze: (alertId: string, minutes: StaffAlertSnoozeMinutes) => snooze.mutate({ alertId, minutes }),
  };

  return (
    <>
      <Popover open={open} onOpenChange={changeOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" className="relative" aria-label={dueLabel ? `${bellLabel}. ${dueLabel}` : bellLabel}>
            {badge ? <BellRing className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
            {badge && (
              <span className="absolute -end-0.5 -top-0.5 min-w-[1.1rem] rounded-full bg-destructive px-1 text-center text-[10px] font-semibold leading-[1.1rem] text-destructive-foreground tabular">
                {badge}
              </span>
            )}
            {dueLabel && <span className="absolute bottom-1 end-1 h-2 w-2 rounded-full bg-warning ring-2 ring-background" aria-hidden />}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[24rem] max-w-[calc(100vw-1rem)] p-0">
          <div className="flex items-center gap-2 border-b px-3 py-2">
            <span className="text-sm font-semibold">{t('staff_alert.bell')}</span>
            {badge && <span className="text-xs text-muted-foreground">{t('staff_alert.unread', { count: unread ?? 0 })}</span>}
            {hideNonCritical && view.hidden > 0 && (
              <Button size="xs" variant="ghost" className="ms-auto" onClick={() => setHideNonCritical(false)}>
                <Eye /> {t('staff_alert.show_hidden')}
              </Button>
            )}
          </div>
          <DueStrips strips={due.strips} onOpen={openPath} />
          <div className="max-h-[70vh] space-y-2 overflow-y-auto p-2">
            {alerts.length === 0 && <p className="px-2 py-6 text-center text-sm text-muted-foreground">{t('staff_alert.empty')}</p>}
            {alerts.map((alert) => (
              <StaffAlertCard key={alert.id} alert={alert} compact {...cardProps} />
            ))}
            {feed.hasNextPage && (
              <Button size="sm" variant="outline" className="w-full" loading={feed.isFetchingNextPage} onClick={() => void feed.fetchNextPage()}>
                {t('staff_alert.load_more')}
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {!open &&
        view.shown.length > 0 &&
        createPortal(
          <div role="region" aria-label={t('staff_alert.bell')} aria-live="polite" className="fixed bottom-4 end-4 z-50 flex w-[22rem] max-w-[calc(100vw-2rem)] flex-col gap-2 no-print">
            {view.shown.map((alert) => (
              <StaffAlertCard key={alert.id} alert={alert} {...cardProps} />
            ))}
            {(more > 0 || (hasNonCritical && !hideNonCritical)) && (
              <div className="flex flex-wrap items-center justify-end gap-1.5">
                {hasNonCritical && !hideNonCritical && (
                  <Button size="xs" variant="ghost" className="bg-card/90 shadow-sm" onClick={() => setHideNonCritical(true)}>
                    <EyeOff /> {t('staff_alert.hide_others')}
                  </Button>
                )}
                {more > 0 && (
                  <Button size="xs" variant="secondary" className="shadow-sm" onClick={() => changeOpen(true)}>
                    {t('staff_alert.more', { count: more })}
                  </Button>
                )}
              </div>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}

function StaffAlertCard({
  alert,
  compact,
  t,
  onOpen,
  onAcknowledge,
  onSnooze,
}: {
  alert: StaffAlertDto;
  compact?: boolean;
  t: Translator['t'];
  onOpen: (path: string) => void;
  onAcknowledge: (alertId: string) => void;
  onSnooze: (alertId: string, minutes: StaffAlertSnoozeMinutes) => void;
}) {
  const [choosingSnooze, setChoosingSnooze] = useState(false);
  const tone = severityTone(alert.severity);
  const path = staffAlertPath(alert.ref);
  const segments = messageSegments(t(alert.messageKey, boldParams(alert.params)));

  return (
    <article
      className={cn(
        'rounded-lg border border-s-4 bg-card p-3 text-sm text-card-foreground',
        compact ? 'shadow-none' : 'shadow-lg',
        tone === 'destructive' && 'border-s-destructive',
        tone === 'warning' && 'border-s-warning',
        tone === 'info' && 'border-s-info',
      )}
    >
      <div className="mb-1.5 flex items-center gap-2">
        <StatusBadge status={alert.severity} tone={tone} label={t(`staff_alert.severity.${alert.severity}`)} />
        {!alert.personal && <span className="text-xs text-muted-foreground">{t('staff_alert.via_ward')}</span>}
        <span className="ms-auto text-xs text-muted-foreground tabular">{relative(alert.createdAt)}</span>
      </div>
      <p className="leading-snug [overflow-wrap:anywhere]">
        {segments.map((segment) =>
          segment.bold ? (
            <strong key={segment.at} className="font-semibold">
              {segment.text}
            </strong>
          ) : (
            <span key={segment.at}>{segment.text}</span>
          ),
        )}
      </p>
      {alert.ackedAt && (
        <p className="mt-1 text-xs text-muted-foreground">{alert.ackedByName ? t('staff_alert.acked_by', { name: alert.ackedByName }) : t('staff_alert.acked')}</p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {path && (
          <Button size="xs" variant="outline" onClick={() => onOpen(path)}>
            <ExternalLink className="rtl:-scale-x-100" /> {t('staff_alert.open')}
          </Button>
        )}
        <Button size="xs" variant={alert.severity === 'critical' ? 'destructive' : 'secondary'} onClick={() => onAcknowledge(alert.id)}>
          <Check /> {alert.severity === 'info' ? t('staff_alert.dismiss') : t('staff_alert.acknowledge')}
        </Button>
        {alert.severity !== 'info' && !choosingSnooze && (
          <Button size="xs" variant="ghost" onClick={() => setChoosingSnooze(true)}>
            <AlarmClock /> {t('staff_alert.snooze')}
          </Button>
        )}
      </div>
      {choosingSnooze && (
        <div role="group" aria-label={t('staff_alert.snooze')} className="mt-2 flex flex-wrap gap-1.5">
          {STAFF_ALERT_SNOOZE_MINUTES.map((minutes) => (
            <Button key={minutes} size="xs" variant="outline" onClick={() => onSnooze(alert.id, minutes)}>
              {t('staff_alert.snooze_minutes', { minutes })}
            </Button>
          ))}
        </div>
      )}
    </article>
  );
}
